import React, { useCallback, useState } from 'react';
import { PapiDeviation } from '@wix/da-papi-types';
import { BiData, DeviationDeleteClickBiEvent } from '@wix/da-bi/pkg/events';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import {
  getDeviationResourceType,
  getDeviationType,
} from '../../../DeviationViews/_helpers';
import { DeviationTypes } from '../../../types/deviation';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { useDeleteDeviationAction } from './useDeleteDeviationAction';
import {
  DeleteDeviationPayload,
  OnUnpublishCompleteAction,
  unpublishDeviation,
} from '../../../redux/deviations/actionCreators';
import TierCreatorReduxModule from '../../../Tier/redux/creator/ModuleLoadable';

interface DeleteDeviationFlowOptions {
  redirectTo?: DeleteDeviationPayload['redirectTo'];
  shouldRedirect?: DeleteDeviationPayload['shouldRedirect'];
  /**
   * Adoptables offer a slightly different delete flow when they've been adopted. It asks the user if they want to unlist
   * the deviation first, and that unlist action has an alternate set of terminating behaviours.
   */
  onUnpublishCompleteAction?: OnUnpublishCompleteAction;
  /**
   * Will add a tier-related redux module to {@link useDeleteDeviationFlow}'s {@link supportingComponents}
   */
  supportTierDeletion: boolean;
}

/**
 * Provides a callback for enacting our deviation deletion flow. Includes an accompanying confirmation modal for
 * deviation types that require it.
 */
export function useDeleteDeviationFlow(
  deviation: PapiDeviation,
  {
    shouldRedirect = true,
    redirectTo,
    supportTierDeletion,
    onUnpublishCompleteAction = OnUnpublishCompleteAction.ReloadStashPage,
  }: DeleteDeviationFlowOptions
) {
  const dispatchDeleteDeviationAction = useDeleteDeviationAction();
  const dispatchUnpublishDeviation = useAction(unpublishDeviation);
  const [isShowingConfirmation, setShowingConfirmation] = useState(false);

  const enactFlow = useCallback(() => {
    const type = getDeviationType(deviation);
    if (type === DeviationTypes.TIER) {
      dispatchDeleteDeviationAction({ deviation, shouldRedirect, redirectTo });
    } else {
      setShowingConfirmation(true);
    }
  }, [dispatchDeleteDeviationAction, deviation, shouldRedirect, redirectTo]);

  const handleConfirm = useCallback(
    (shouldUnpublishInstead: boolean) => {
      setShowingConfirmation(false);
      if (shouldUnpublishInstead) {
        dispatchUnpublishDeviation({
          deviation,
          onCompleteAction: onUnpublishCompleteAction,
        });
      } else {
        dispatchDeleteDeviationAction({
          deviation,
          shouldRedirect,
          redirectTo,
        });
      }
    },
    [
      onUnpublishCompleteAction,
      dispatchDeleteDeviationAction,
      dispatchUnpublishDeviation,
      deviation,
      shouldRedirect,
      redirectTo,
    ]
  );

  const handleCancel = useCallback(() => {
    setShowingConfirmation(false);
  }, []);

  return {
    /**
     * Depending on deviation type, this callback will either dispatch a delete action or show a confirmation modal.
     */
    enactFlow,

    /**
     * biData you'd add to the button that beings the delete flow.
     */
    biData: BiData<DeviationDeleteClickBiEvent>({
      evid: 372,
      typeid: getDeviationResourceType(deviation),
      itemid: deviation.deviationId,
    }),

    /**
     * The necessary supporting components. e.g modals, redux modules
     *
     * If you don't render this in your component, this hook won't work.
     */
    supportingComponents: (
      <>
        {supportTierDeletion && <TierCreatorReduxModule />}
        <DeleteConfirmationModal
          key="delete-confirmation-modal"
          deviation={deviation}
          isOpen={isShowingConfirmation}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      </>
    ),
  };
}
